/**
 * WAHA Proxy Service
 *
 * Tutte le chiamate API WAHA passano dalla edge function waha-proxy
 * così le credenziali WAHA (API key e secret) restano server-side.
 */

import { supabase } from '../supabase';
import { WAHA_WEBHOOK_EVENTS } from './whatsapp/wahaWebhookEvents';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const WAHA_URL = import.meta.env.VITE_WAHA_URL || '';

// Status codes che meritano un retry (errori transitori)
const RETRYABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000; // 1s, 2s, 4s (exponential backoff)

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function callWahaProxy(params: {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body?: any;
  injectWahaSecret?: boolean;
  signal?: AbortSignal;
}): Promise<Response> {
  const { signal, ...proxyParams } = params;

  const getToken = async (forceRefresh = false): Promise<string> => {
    if (forceRefresh) {
      const { data: { session } } = await supabase.auth.refreshSession();
      if (session?.access_token) return session.access_token;
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    return session.access_token;
  };

  const doFetch = async (token: string) => {
    return fetch(`${SUPABASE_URL}/functions/v1/waha-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(proxyParams),
      signal,
    });
  };

  let token = await getToken();
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await doFetch(token);

      // 401: token scaduto → refresh e riprova subito (1 volta sola)
      if (response.status === 401 && attempt === 0) {
        token = await getToken(true);
        continue;
      }

      // Successo o errore non-retryable → ritorna subito
      if (!RETRYABLE_STATUS.has(response.status) || attempt === MAX_RETRIES) {
        return response;
      }

      // Errore retryable → aspetta con exponential backoff
      lastResponse = response;
      const delay = BASE_DELAY_MS * Math.pow(2, attempt);
      console.debug(`[WAHA] Retry ${attempt + 1}/${MAX_RETRIES} dopo ${response.status} (attesa ${delay}ms): ${params.path}`);
      await sleep(delay);

    } catch (error) {
      // Errori di rete (fetch failed, CORS, abort)
      if (signal?.aborted || attempt === MAX_RETRIES) throw error;

      const delay = BASE_DELAY_MS * Math.pow(2, attempt);
      console.debug(`[WAHA] Retry ${attempt + 1}/${MAX_RETRIES} dopo network error (attesa ${delay}ms): ${params.path}`);
      await sleep(delay);
    }
  }

  // Fallback: ritorna l'ultima risposta ricevuta
  return lastResponse!;
}

/**
 * Test connessione a WAHA
 */
export async function testWahaConnection(): Promise<boolean> {
  try {
    const response = await callWahaProxy({
      method: 'GET',
      path: '/api/sessions'
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Elimina una sessione WAHA
 */
export async function deleteWahaSession(sessionName: string): Promise<boolean> {
  try {
    const response = await callWahaProxy({
      method: 'DELETE',
      path: `/api/sessions/${sessionName}`
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Crea una sessione WAHA con webhook configurato.
 * Il WAHA_SECRET viene iniettato server-side.
 */
export async function createWahaSession(
  sessionName: string,
  configId: string,
  supabaseAnonKey: string
): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL;
  const webhookUrl = apiGatewayUrl
    ? `${apiGatewayUrl}/webhooks/waha-webhook-v2`
    : `${supabaseUrl}/functions/v1/waha-webhook-v2`;

  const body = {
    name: sessionName,
    config: {
      noweb: {
        store: { enabled: true, fullSync: true },
        merge: true
      },
      webjs: {
        tagsEventsOn: true
      },
      ignore: {
        channels: true,
        broadcast: true
      },
      webhooks: [{
        url: webhookUrl,
        // Lista unica degli eventi (gruppi, chiamate, etichette: tutti gestiti dal core)
        events: [...WAHA_WEBHOOK_EVENTS],
        // Niente hmac: il core verifica solo x-waha-secret e il gateway riscrive il corpo, quindi una
        // firma non servirebbe. Prima partiva la chiave letterale "__INJECT_SERVER_SIDE__" (mai sostituita).
        customHeaders: [
          { name: 'authorization', value: `Bearer ${supabaseAnonKey}` },
          { name: 'x-waha-secret', value: '__INJECT_SERVER_SIDE__' },
          { name: 'x-config-id', value: configId }
        ],
        retries: { policy: 'exponential', delaySeconds: 2, attempts: 10 }
      }]
    }
  };

  const response = await callWahaProxy({
    method: 'POST',
    path: '/api/sessions',
    body,
    injectWahaSecret: true
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Errore creazione sessione: ${response.status} ${errorText}`);
  }
}

/**
 * Avvia una sessione WAHA
 */
export async function startWahaSession(sessionName: string): Promise<void> {
  const response = await callWahaProxy({
    method: 'POST',
    path: `/api/sessions/${sessionName}/start`
  });

  if (!response.ok) {
    throw new Error(`Errore avvio sessione: ${response.status}`);
  }
}

/**
 * Ottieni QR code per una sessione, come base64 dell'immagine PNG (senza prefisso data:).
 * Specifica WAHA 2026.10: format ammessi solo 'image' o 'raw' (prima si mandava 'base64').
 * Con format=image WAHA risponde con il PNG binario (il proxy lo inoltra cosi' com'e')
 * oppure, se si chiede JSON, con Base64File { mimetype, data }: si leggono entrambi.
 */
export async function getWahaQRCode(sessionName: string): Promise<string> {
  const response = await callWahaProxy({
    method: 'GET',
    path: `/api/${sessionName}/auth/qr?format=image`
  });

  if (!response.ok) {
    throw new Error(`Errore recupero QR: ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('image/')) {
    const blob = await response.blob();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  } else if (contentType.includes('application/json')) {
    const data = await response.json() as { mimetype?: string; data?: string };
    if (typeof data?.data === 'string' && data.data) {
      return data.data.includes(',') ? data.data.split(',')[1] : data.data;
    }
    throw new Error('Errore recupero QR: risposta senza immagine');
  } else {
    const text = await response.text();
    return text.replace(/[^A-Za-z0-9+/=]/g, '');
  }
}

/**
 * Ottieni status sessione WAHA
 */
export async function getWahaSessionStatus(sessionName: string): Promise<string> {
  try {
    const response = await callWahaProxy({
      method: 'GET',
      path: `/api/sessions/${sessionName}`
    });

    if (!response.ok) {
      if (response.status === 404) return 'NOT_FOUND';
      return 'FAILED';
    }

    const data = await response.json();
    return data.status || 'FAILED';
  } catch {
    return 'FAILED';
  }
}

/**
 * Ottieni dettagli sessione (incluso numero telefono)
 */
export async function getWahaSessionDetails(sessionName: string): Promise<{ phoneNumber?: string }> {
  try {
    const response = await callWahaProxy({
      method: 'GET',
      path: `/api/sessions/${sessionName}`
    });

    if (!response.ok) return {};

    const data = await response.json();
    const phoneNumber = data.me?.id || data.me?.user || data.phoneNumber || '';
    return { phoneNumber };
  } catch {
    return {};
  }
}

/**
 * Ottieni URL base WAHA (solo URL, senza credenziali)
 */
export function getWahaUrl(): string {
  return WAHA_URL;
}
